import React, { type FC } from "react";

import {
	IconRelationBoth,
	IconRelationGrouped,
	IconRelationIndirect,
	IconRelationNone,
	IconRelationRight,
} from "~/components/icon";
import { HealthStateValue, Type } from "~/models/dto";
import { cx } from "~/tools/class-variance";

import { healthStateToColorToken } from "../../../common/functions";
import { Api } from "../../../common/openapi";
import { type IconSize, getSizeForIcon } from "../icon/IconSizes";

import DependencyDirection = Api.DependencyDirection;

export type IconRelationTypes = Type.Relation | Type.IndirectRelation | Type.GroupRelation;

export interface StateRelationArrowIconProps {
	className?: string;
	type: IconRelationTypes;
	healthState?: HealthStateValue | undefined;
	dependencyDirection?: DependencyDirection;
	iconSize?: IconSize;
}

export const StateRelationArrowIcon: FC<StateRelationArrowIconProps> = ({
	className,
	type,
	healthState = HealthStateValue.Unknown,
	dependencyDirection = DependencyDirection.None,
	iconSize = "default",
}) => (
	<div
		className={cx(
			"relative flex h-(--height) w-7.25 items-center justify-center text-(--health-state-color)",
			className,
		)}
		data-testid="arrow-container"
		style={
			{
				"--height": getSizeForIcon(iconSize),
				"--health-state-color": `var(${healthStateToColorToken(healthState)})`,
			} as React.CSSProperties
		}
	>
		<ArrowIcon className="text-[32px]" dependencyDirection={dependencyDirection} relationType={type} />
	</div>
);

const ArrowIcon: React.FC<{
	className?: string;
	relationType: IconRelationTypes;
	dependencyDirection: DependencyDirection;
}> = ({ relationType, dependencyDirection, ...rest }) => {
	if (relationType === Type.IndirectRelation) {
		return <IconRelationIndirect data-testid="icon-relation-indirect" {...rest} />;
	}

	if (relationType === Type.GroupRelation) {
		return <IconRelationGrouped data-testid="icon-relation-grouped" {...rest} />;
	}

	if (dependencyDirection === DependencyDirection.OneWay) {
		return <IconRelationRight data-testid="icon-relation-one-way" {...rest} />;
	}

	if (dependencyDirection === DependencyDirection.Both) {
		return <IconRelationBoth data-testid="icon-relation-both-way" {...rest} />;
	}

	return <IconRelationNone data-testid="icon-relation-none-way" {...rest} />;
};
